import {
  FOLDER_ROOT_PATH,
  folderEntryPath,
  FolderPath,
  LibraryFolderEntry,
  resolveFolderPath,
} from '@lib/library/folderPath';
import { createContext } from '@lib/mobx/context';
import { DataFrame } from '@lib/mobx/dataframe';
import {
  isBilledToAnotherAccount as billedToAnotherAccount,
  BillingOwnership,
  resolveBillingOwnership,
} from '@lib/utils/billingOwnership';
import {
  formatOwnerName,
  LibraryOwnership,
  isOwnedByAnotherUser as ownedByAnotherUser,
  resolveLibraryOwnership,
} from '@lib/utils/libraryOwnership';
import { DAY } from '@lib/utils/time';
import { Media, MediaStatus } from '@typings/Media';
import { decodeJwt } from 'jose';
import { action, computed, makeObservable, observable } from 'mobx';
import { billingStore } from '../Billing';
import { userStore } from '../User';
import { LibrarySearchStore } from './LibrarySearch';

export type Library = {
  _id: string;
  name: string;

  settings: {
    v1: {
      visualEngine?: 'alpha' | 'helium';
      textTranscriptEngine?: 'hydrogen';
      glossary?: string[];
      /**
       * Backend-declared feature skips. `skip.visuals === true` means the
       * library has no visual content (e.g. podcasts, audio-only libraries)
       * so visual and unified search modes cannot return results for it.
       * Authoritative; use this for feature gating rather than inferring
       * from media type.
       */
      skip?: {
        visuals?: boolean;
      };
    };
  };

  /**
   * Billing account that ingest and storage for this library are charged to — NOT necessarily
   * the uploader's. This, and not `owner` below, is what answers "will this upload spend *my*
   * credits?": on an org or SSO account several users share one billing account, so a
   * teammate's library has a different `owner` and the same `billingId`, and the uploader's own
   * balance is exactly the one that gets spent. Read through
   * `LibraryStore.isBilledToAnotherAccount`; see `resolveBillingOwnership` for why the two axes
   * must not be collapsed.
   *
   * Optional defensively, not genuinely: it *is* in the `required` set of the library response
   * schema, but a missing id has to resolve to `unknown` rather than throw.
   */
  billingId?: string;

  createdAt: number;
  updatedAt: number;

  filters: unknown; // tbd

  /**
   * Arrives inline on `GET /library`, but a `LibraryStore` can exist before it
   * lands — optional so the compiler enforces the guards that call sites already
   * carry (the sort comparators and every stat cell render a dash for absent
   * values).
   */
  stats?: {
    count: number;
    mediaSize: number;
    mediaDuration: number;

    breakdown: Partial<
      Record<
        MediaStatus,
        {
          count: number;
          mediaSize: number;
          mediaDuration: number;
        }
      >
    >;
  };

  /**
   * Who the library belongs to. Arrives inline on `GET /library` as well as `GET /library/:id`,
   * so the Libraries page has it without a second fetch. Read it through `ownership` /
   * `otherOwnerName`, never by comparing ids inline.
   *
   * Answers "whose library is this?" and nothing more — **not** "whose credits does adding
   * media here spend?", which is `billingId` above. See `resolveBillingOwnership`.
   *
   * Optional defensively, not genuinely: the schema declares it **required** on both endpoints
   * and we have not confirmed a response that omits it. It is typed optional because a
   * `LibraryStore` exists before its fetch lands and because this repo does not trust a
   * `required` set (see `.claude/rules/general.md`) — not on a claim about which libraries
   * carry it. In particular, do not rely on it being absent on the demo and read-only
   * libraries to keep them out of the surfaces below: `ownership` excludes those explicitly,
   * because if the backend does name an owner for the shared Welcome library then every account
   * on the platform would otherwise be told a stranger owns it.
   *
   * Everything inside except `userId` is genuinely optional per the schema.
   */
  owner?: {
    userId: string;
    email?: string;
    name?: {
      first?: string;
      last?: string;
      nickname?: string;
    };
  };

  latestMediaIds: string[];

  /** The library's folder tree, flat. Absent on a library that has never had a folder. */
  folders?: LibraryFolderEntry[];

  system?: {
    demo?: boolean;
    readonly?: boolean;
    default?: boolean;
  };

  userMetadata?: Record<string, any>;
};

/**
 * True when the library only supports transcript search (podcasts /
 * audio-only libraries). Visual and unified search modes cannot
 * return results for such libraries.
 *
 * Keyed off the backend-declared `settings.v1.skip.visuals` flag —
 * that's authoritative. Do not use `userMetadata.podcastLibrary`
 * for feature gating; it's a user-facing label only.
 */
export function isLibraryTranscriptOnly(library: Library): boolean {
  return library.settings?.v1?.skip?.visuals === true;
}

const NO_FOLDERS: readonly LibraryFolderEntry[] = [];

export class LibraryStore extends DataFrame<Library> {
  readonly search: LibrarySearchStore;

  _previewToken: {
    token: string;
    expiresAt: number;
  } | null = null;
  _previewTokenPromise: Promise<string | undefined> | null = null;

  @computed
  get name() {
    return this.data?.name;
  }

  @computed
  get stats() {
    return this.data?.stats;
  }

  @computed
  get latestMediaIds() {
    return this.data?.latestMediaIds;
  }

  /**
   * The library's folder tree, flat, or null until one has arrived. This is the one copy every
   * surface reads, the library page included: `GET /library`, the library page's files listing and
   * every folder write (through `LibraryManager`) each replace it. Kept out of `data` so a failed
   * refetch, which nulls `data`, doesn't take the tree with it.
   */
  @observable.ref
  private _folders: readonly LibraryFolderEntry[] | null = null;

  @action
  setFolders(folders: readonly LibraryFolderEntry[]) {
    this._folders = folders;
  }

  @computed
  get folders(): readonly LibraryFolderEntry[] {
    return this._folders ?? NO_FOLDERS;
  }

  @computed
  get folderPaths(): ReadonlySet<FolderPath> {
    return new Set(this.folders.map(folderEntryPath));
  }

  @computed
  get foldersByPath(): ReadonlyMap<FolderPath, LibraryFolderEntry> {
    return new Map(this.folders.map((folder) => [folderEntryPath(folder), folder]));
  }

  /** How many folders sit at the library's top level. 0 until its tree has arrived. */
  @computed
  get topLevelFolderCount(): number {
    return this.folders.filter((entry) => entry.parent === FOLDER_ROOT_PATH).length;
  }

  /**
   * The folder `media` shows in on the library page (see `resolveFolderPath`), or null until the
   * library's tree has arrived — its folders are what say where a stale path landed.
   */
  folderOf(media: Media): FolderPath | null {
    if (this._folders === null) return null;
    return resolveFolderPath(media.folderPath, this.folderPaths);
  }

  @computed
  get areStatsLoaded() {
    return this.data?.stats !== undefined;
  }

  @computed
  get isDefault() {
    return this.data?.system?.default;
  }

  @computed
  get isDemo() {
    return this.data?.system?.demo;
  }

  @computed
  get isReadOnly() {
    return this.data?.system?.readonly;
  }

  /**
   * Who owns this library. `unknown` until both the library's owner and the signed-in user have
   * landed — see `resolveLibraryOwnership`.
   *
   * Drives the "Owned by X" lines only. It is **not** the billing question: use
   * `isBilledToAnotherAccount` for anything that relaxes a gate or claims whose credits get
   * spent, and see `resolveBillingOwnership` for why the two come apart on an org account.
   *
   * Demo and read-only libraries are forced to `unknown`. They are platform fixtures every
   * account sees, so whoever the backend names on them is not a person the reader has any
   * relationship with — and this is an explicit guard rather than a bet on `owner` being absent
   * there, which the schema's `required` set says it isn't. Get that bet wrong and every
   * account's Welcome card grows a spurious "Owned by …".
   *
   * Note the `DataFrame` caveat that applies to both reads: a single failed fetch nulls `data`
   * (`keepDataOnFetchError` is false), so this can fall back from an answer to `unknown`. Fine
   * here — losing the verdict costs a line of text, never invents one.
   */
  @computed
  get ownership(): LibraryOwnership {
    if (this.isDemo || this.isReadOnly) return 'unknown';
    return resolveLibraryOwnership(this.data?.owner?.userId, userStore.data?._id);
  }

  /**
   * True only when we positively know somebody else owns this library. Gates the "Owned by X"
   * lines; says nothing about who pays.
   */
  @computed
  get isOwnedByAnotherUser() {
    return ownedByAnotherUser(this.ownership);
  }

  /**
   * Which billing account an upload into this library is charged to, relative to the current
   * user's — see `resolveBillingOwnership`. `unknown` until both ids have landed.
   *
   * The user side comes from `billingStore.currentBillingId`, not `userStore.data?.billingId`:
   * `/user/me` is allowed to omit `billingId` (it isn't in the endpoint's `required` set), and
   * the accounts most likely to omit it are the org ones this exists for, so that getter falls
   * back to the billing account on the user's own credit notes.
   */
  @computed
  get billingOwnership(): BillingOwnership {
    return resolveBillingOwnership(this.data?.billingId, billingStore.currentBillingId);
  }

  /**
   * True only when we positively know another billing account foots the bill, so the current
   * user's credit and storage figures don't describe this upload at all. Drives the
   * "billed to the owner" notice, the relaxed upload gates, and the withheld top-up CTA.
   *
   * Deliberately keyed on `billingId` rather than on `isOwnedByAnotherUser`: a teammate's
   * library on a shared org billing account is somebody else's *and* on the reader's own tab,
   * and every one of those three behaviours is wrong there. Stays false while billing ownership
   * is `unknown`, which keeps those surfaces behaving as they did before the feature existed.
   */
  @computed
  get isBilledToAnotherAccount() {
    return billedToAnotherAccount(this.billingOwnership);
  }

  /**
   * The owner's name, **only when that owner isn't you** — otherwise `undefined`.
   *
   * The "isn't you" gate lives here rather than at the call sites on purpose. Four surfaces
   * render this (the library grid, the mobile library list, the libraries table, and the upload
   * notices) and every one of them wants the same thing: a name worth reading, which your own
   * never is. A raw `ownerName` getter would put the gate in four places and let one of them
   * drift into labelling all your own libraries with your own name.
   *
   * Still `undefined` for an owner the backend gave no name and no email — see
   * `formatOwnerName`. That is why the upload surfaces take `isBilledToAnotherAccount` *and*
   * this: the fact survives the name going missing, and it's the fact that must be stated.
   * Those surfaces pair the two deliberately, and the pairing is allowed to disagree — a
   * cross-billed library whose owner we can't name (or who *is* the reader, on a personal
   * account billed elsewhere) gets the notice's nameless wording, which is the sentence it
   * shipped with.
   */
  @computed
  get otherOwnerName(): string | undefined {
    if (!this.isOwnedByAnotherUser) return undefined;
    return formatOwnerName(this.data?.owner);
  }

  /** True when the library skips visual indexing, so only transcript search works. */
  @computed
  get isTranscriptOnly() {
    return !!this.data && isLibraryTranscriptOnly(this.data);
  }

  /**
   * True when the library holds nothing at all — a dead end for anything that
   * *reads* media (searching it, handing it to an Agent), though a perfectly
   * ordinary target to upload *into*.
   *
   * Deliberately false while `stats` is undefined. Stats arrive inline on
   * `GET /library`, but a store can exist before they land, and both readers want
   * the same fail-open: the picker must not grey out the whole list during a slow
   * load, and the Moments empty state must not tell you your libraries are empty
   * when it simply doesn't know yet.
   *
   * One getter rather than the test written out at each call site, so the picker
   * (`emptyLibraryDisabledReason`) cannot call a library empty while
   * `multiLibSearch.zeroResultReason` disagrees.
   */
  @computed
  get isEmpty() {
    return this.stats?.count === 0;
  }

  /**
   * Media in this library that a search cannot reach yet — everything that has not
   * finished and has not failed.
   *
   * Derived by **subtracting the terminal states** rather than by naming the
   * pre-searchable ones, which is the whole point. Search runs against the vector
   * collections, and those only hold points for media that finished processing, so
   * "not terminal" is exactly "contributes nothing to a search however you phrase
   * it". Naming the states instead means choosing which ones to trust, and there is
   * no correct choice: `empty` and `uploading` are where a live upload sits *and*
   * where a failed one is abandoned forever, indistinguishable from `stats` alone
   * (`isFailedOrphanUpload` needs the client's upload status to tell them apart).
   * Excluding them made a live upload answer "No moments match"; including them makes
   * an abandoned row answer "still processing".
   *
   * Including them is the better of the two, and the reason is the offer rather than
   * the wording: the state's action is "Go to library", which is where an abandoned
   * row needs to be dealt with anyway. It stopped being a dead end, so being wrong in
   * this direction now costs a slightly optimistic heading rather than trapping the
   * user. `backendWorkingCount` below is what earns the confident wording.
   *
   * Staleness cuts the safe way. Stats are only refreshed at upload / import / delete,
   * so this can read stale-high after media has quietly finished — but a finished
   * library returns results, and results outrank every empty state, so a stale count
   * is only ever consulted when the search genuinely found nothing.
   */
  @computed
  get mediaNotReadyCount() {
    const total = this.stats?.count ?? 0;
    if (total === 0) return 0;
    const b = this.stats?.breakdown;
    const terminal =
      (b?.completed?.count ?? 0) + (b?.errored?.count ?? 0) + (b?.cancelled?.count ?? 0);
    return Math.max(0, total - terminal);
  }

  /**
   * Media the backend has and is actively working through.
   *
   * Narrower than `mediaNotReadyCount` on purpose, and the difference is how much we
   * are entitled to claim: this is the only stats-derived signal that justifies saying
   * "still processing", because these two states mean the backend has the file and is
   * doing something with it. A row that is merely non-terminal earns the vaguer
   * "isn't ready to search yet" instead.
   */
  @computed
  get mediaBackendWorkingCount() {
    const b = this.stats?.breakdown;
    if (!b) return 0;
    return (b.queued?.count ?? 0) + (b.processing?.count ?? 0);
  }

  /**
   * True when the library holds media but none of it is, or will become,
   * searchable — every row errored or was cancelled, and nothing is still on its
   * way.
   *
   * Distinct from `isEmpty`, which is false here: `stats.count` counts the failed
   * rows (they persist until the user clears them), so a library of nothing but
   * failed uploads looks populated while being just as unsearchable. Without this
   * the empty state would blame the user's query for it.
   */
  @computed
  get hasOnlyFailedMedia() {
    const total = this.stats?.count ?? 0;
    if (total === 0) return false;
    if (this.mediaNotReadyCount > 0) return false;
    const b = this.stats?.breakdown;
    const dead = (b?.errored?.count ?? 0) + (b?.cancelled?.count ?? 0);
    return dead >= total;
  }

  setThumbnail(thumbnailMediaId: string) {
    if (!this.data) return;
    this.data.userMetadata = {
      ...(this.data.userMetadata || {}),
      thumbnailMediaId,
    };
    // No local `updatedAt` write — same reasoning as LibraryManager.modifyLibrary:
    // the backend owns it, and the Libraries list view now displays it.
  }

  @computed
  get thumbnailMediaId() {
    return this.data?.userMetadata?.thumbnailMediaId || this.latestMediaIds?.[0] || null;
  }

  async getValidPreviewToken(): Promise<string> {
    const now = Date.now();

    // Check if we have a valid token that's not about to expire
    if (this._previewToken && this._previewToken.expiresAt > now + DAY) {
      return this._previewToken.token;
    }

    // If a refresh is already in progress, await it
    if (this._previewTokenPromise) {
      const token = await this._previewTokenPromise;
      // If the promise resolved to undefined (constructor error case), start a new fetch
      if (token !== undefined) {
        return token;
      }
      // Promise resolved to undefined. Check if we now have a valid cached token
      // (another concurrent call might have succeeded)
      if (this._previewToken && this._previewToken.expiresAt > Date.now() + DAY) {
        return this._previewToken.token;
      }
      // Otherwise, fall through to start a new fetch
    }

    // At this point, either:
    // 1. No token exists (this._previewToken is null), OR
    // 2. Token expires within 24 hours (we already returned if it expires later)
    // So we should refresh the token (proactively if expiring soon, or reactively if expired)
    this._previewTokenPromise = this.fetchPreviewToken()
      .then((token) => {
        const expiresAt = this.getPreviewTokenExpiry(token);
        this._previewToken = { token, expiresAt };

        // Store token in localStorage
        const storageKey = this.getPreviewTokenStorageKey();
        localStorage.setItem(storageKey, token);

        this._previewTokenPromise = null;
        return token;
      })
      .catch((error) => {
        // Clear the promise on error so we can retry
        this._previewTokenPromise = null;

        // If fetch failed but we have a valid token that hasn't expired yet, use it
        if (this._previewToken && this._previewToken.expiresAt > Date.now()) {
          console.warn(
            `Preview token refresh failed for library ${this.libraryId}, using existing valid token:`,
            error,
          );
          return this._previewToken.token;
        }

        // No valid fallback, re-throw
        throw error;
      });

    // This promise always resolves to a string (or throws), never undefined
    return this._previewTokenPromise as unknown as Promise<string>;
  }

  protected getPreviewTokenExpiry(token: string) {
    try {
      const decoded = decodeJwt(token);
      return decoded.exp ? decoded.exp * 1000 : Date.now() + 1000 * 60 * 60 * 3; // Convert exp from seconds to milliseconds, fallback to 3h
    } catch (error) {
      // If token is invalid/corrupted, throw a more descriptive error
      throw new Error(
        `Invalid preview token: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  protected getPreviewTokenStorageKey() {
    return `preview-token-${this.libraryId}`;
  }

  constructor(
    readonly libraryId: string,
    library?: Library,
  ) {
    super({ autoFetch: library ? 'off' : 'nextTick' });
    makeObservable(this);
    this.search = new LibrarySearchStore(this);

    if (library) {
      this.data = library;
      this._folders = library.folders ?? NO_FOLDERS;
    }

    // Load token from localStorage if it exists
    const storageKey = this.getPreviewTokenStorageKey();
    const storedToken = localStorage.getItem(storageKey);
    if (storedToken) {
      try {
        this._previewToken = {
          token: storedToken,
          expiresAt: this.getPreviewTokenExpiry(storedToken),
        };
      } catch (error) {
        // If token is corrupted/invalid, remove it from storage and fetch a new one
        console.warn(
          `Invalid preview token for library ${this.libraryId}, removing from storage and fetching new token:`,
          error,
        );
        localStorage.removeItem(storageKey);
        this._previewToken = null;
        // Fetch a new token and store the promise so concurrent calls can await it
        // We can't await in constructor, but we can set up the promise properly
        this._previewTokenPromise = this.fetchPreviewToken()
          .then((token) => {
            try {
              const expiresAt = this.getPreviewTokenExpiry(token);
              this._previewToken = { token, expiresAt };
              localStorage.setItem(storageKey, token);
              this._previewTokenPromise = null;
              return token;
            } catch (error) {
              console.warn('Failed to parse fetched token:', error);
              this._previewTokenPromise = null;
              return undefined;
            }
          })
          .catch((fetchError) => {
            // If fetch fails, clear the promise so it can be retried when getValidPreviewToken() is called
            console.warn(
              'Failed to fetch new preview token after clearing corrupted one:',
              fetchError,
            );
            this._previewTokenPromise = null;
            // Don't re-throw - we're in constructor and have no caller to handle this.
            // getValidPreviewToken() will trigger a fresh fetch when needed.
            return undefined;
          });
      }
    }
  }

  protected async fetch(): Promise<Library> {
    const library = await userStore.apiWithAuth<Library>(`/library/${this.libraryId}`);
    return library;
  }

  protected override postProcess(library: Library): Library {
    this.setFolders(library.folders ?? NO_FOLDERS);
    return library;
  }

  protected async fetchPreviewToken(): Promise<string> {
    const { previewToken } = await userStore.apiWithAuth<{ previewToken: string }>(
      `/library/${this.libraryId}/preview`,
    );
    return previewToken;
  }
}

export const { Provider: LibraryProvider, useStore: useLibrary } = createContext(LibraryStore, {
  dispose: (store) => store.deinit(),
});
